// pages/routing/RoutingClients.jsx — SOUND-826 Phase 1
//
// The client picker for the read-only CheckoutChamp routing mirror. A client is
// listed iff it has an ACTIVE checkoutchamp pipeline config — the API owns that
// scope; this page renders whatever /routing/clients returns.
import React from "react";
import { useNavigate } from "react-router-dom";
import { Route as RouteIcon } from "lucide-react";
import PageHeader from "components/PageLayout";
import RecordTable from "components/Records/RecordTable";
import RelativeTime from "components/RelativeTime";
import { useRoutingClients } from "hooks/useRouting";
import { countsSummary, unreadableTypes, changedBanner } from "utilities/routingView";

export default function RoutingClients() {
  const navigate = useNavigate();
  const { clients, loading, error, refetch } = useRoutingClients();

  const columns = [
    {
      key: "name",
      header: "Client",
      width: "22%",
      exportValue: (c) => c.name || c.slug,
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{c.name || c.slug || c.companyId}</div>
          {c.slug && <div className="truncate text-xs text-muted-foreground">{c.slug}</div>}
        </div>
      ),
    },
    {
      key: "lastCheckedAt",
      header: "Last checked",
      width: "13%",
      cell: (c) => <RelativeTime value={c.latest?.lastCheckedAt || c.latest?.takenAt} fallback="Never" />,
    },
    {
      key: "takenAt",
      header: "Snapshot taken",
      width: "13%",
      cell: (c) => <RelativeTime value={c.latest?.takenAt} fallback="—" />,
    },
    {
      key: "counts",
      header: "Objects",
      width: "32%",
      exportValue: (c) => countsSummary(c.latest?.counts),
      cell: (c) => (
        <span className="text-muted-foreground">{countsSummary(c.latest?.counts) || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "20%",
      truncate: false,
      title: (c) => {
        const denied = unreadableTypes(c.latest?.access);
        return [
          denied.length ? `No API access: ${denied.join(", ")}` : null,
          changedBanner(c.latest?.changedSincePrevious),
        ]
          .filter(Boolean)
          .join("\n") || undefined;
      },
      cell: (c) => {
        if (!c.latest) return <span className="badge badge-muted">No snapshot yet</span>;
        const denied = unreadableTypes(c.latest.access);
        const changed = (c.latest.changedSincePrevious || []).length;
        return (
          <div className="flex flex-wrap gap-1">
            {denied.length > 0 && (
              <span className="badge badge-warning">
                {denied.length} of 6 unreadable
              </span>
            )}
            {changed > 0 && <span className="badge badge-info">Changed</span>}
            {!denied.length && !changed && <span className="badge badge-success">Full access</span>}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Routing"
        subtitle="CheckoutChamp gateway routing per client, mirrored read-only. Edits still happen in CheckoutChamp."
      />
      <div className="container py-6">
        <RecordTable
          columns={columns}
          rows={clients}
          rowKey={(c) => c.companyId}
          onRowClick={(c) => navigate(`/routing/${c.companyId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          skeletonRows={6}
          empty={{
            icon: RouteIcon,
            heading: "No CheckoutChamp clients",
            description: "A client appears here once it has an active CheckoutChamp pipeline config.",
          }}
        />
      </div>
    </div>
  );
}
